import { useAuthStore } from "@/lib/auth-store";
import { PERMISOS } from "@/lib/permisos";
import { useDashboardLayoutStore } from "../stores/dashboard-layout-store";
import { useHealthCheck } from "../hooks/use-health-check";
import { AccionesRapidasCard } from "./AccionesRapidasCard";
import { ActividadRecienteCard } from "./ActividadRecienteCard";
import { DistribucionEstadosCard } from "./DistribucionEstadosCard";
import { MontoEnAutorizacionCard } from "./MontoEnAutorizacionCard";
import { ProximasAVencerCard } from "./ProximasAVencerCard";
import { RecursosCard } from "./RecursosCard";
import { StatsGrid } from "./StatsGrid";
import { SystemHealthCard } from "./SystemHealthCard";

/**
 * Dashboard del portal · landing tras el login. Estructura alineada al diseño
 * de referencia: saludo → (Acciones rápidas | Mis últimas solicitudes) 50/50 →
 * 4 KPIs → (Próximas + Monto apilados | Distribución por etapa). La visibilidad
 * de cada bloque se personaliza desde el modal "Customize Layout" del sidebar
 * (store `useDashboardLayoutStore`).
 */
export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const roles = user?.roles ?? [];
  const permisos = user?.permisos ?? [];
  // Sección de operación/estado del sistema · atada a la configuración (solo un
  // administrador del sistema la tiene). Reemplaza el chequeo por rol "Administrador".
  const esAdmin = permisos.includes(PERMISOS.adminConfiguraciones);

  // Los bloques centrados en solicitudes (mis últimas solicitudes / bandeja +
  // los KPIs) solo aplican a quien participa en el flujo de solicitudes (puede
  // gestionarlas, verlas todas o autorizarlas). Un rol de solo lectura como Visor
  // no los ve (ni el CTA de "crear la primera").
  const trabajaConSolicitudes = [
    PERMISOS.solicitudesGestionar,
    PERMISOS.solicitudesVerTodas,
    PERMISOS.autorizacionAutorizar,
  ].some((p) => permisos.includes(p));

  // El estado del sistema (ops) solo lo ve Administración · no polleamos para
  // los demás roles.
  const ready = useHealthCheck(esAdmin);
  const sistemaOk = !ready.isError;
  const nombre = user?.fullName?.split(" ")[0] || user?.email?.split("@")[0] || "Usuario";

  const widgets = useDashboardLayoutStore((s) => s.widgets);

  return (
    <div className="mx-auto w-full max-w-7xl space-y-4 px-4 py-6 md:px-6">
      {/* Saludo */}
      <div>
        <h1 className="text-xl font-extrabold tracking-tight text-foreground sm:text-2xl">¡Hola, {nombre}!</h1>
        <p className="text-xs font-medium text-muted-foreground sm:text-sm">Aquí tienes un resumen de tu actividad reciente.</p>
      </div>

      {/* Fila 1 · Acciones rápidas | Mis últimas solicitudes (50/50) */}
      {widgets.quickActions || widgets.recentRequests ? (
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-12">
          {widgets.quickActions ? (
            <div className="lg:col-span-6">
              <AccionesRapidasCard roles={roles} permisos={permisos} />
            </div>
          ) : null}
          {widgets.recentRequests && trabajaConSolicitudes ? (
            <div className="lg:col-span-6">
              <ActividadRecienteCard />
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Fila 2 · 4 KPIs */}
      {widgets.kpiRow1 && trabajaConSolicitudes ? <StatsGrid /> : null}

      {/* Fila 3 · Próximas + Monto apilados | Distribución por etapa */}
      {widgets.kpiRow2 && trabajaConSolicitudes ? (
        <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-12">
          <div className="flex flex-col justify-between gap-4 md:col-span-4">
            <ProximasAVencerCard />
            <MontoEnAutorizacionCard />
          </div>
          {widgets.stageDistribution ? (
            <div className="md:col-span-8">
              <DistribucionEstadosCard />
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Estado del sistema + recursos · solo Administración (fijo al final). */}
      {esAdmin ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <SystemHealthCard ok={sistemaOk} fetching={ready.isFetching} pending={ready.isPending} />
          <RecursosCard esAdmin={esAdmin} />
        </div>
      ) : null}
    </div>
  );
}
